import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';

interface ImageZoomPanFrameProps {
  src: string;
  alt?: string;
  rotation?: number;
  zoom?: number;
  className?: string;
  onError: () => void;
  onLoad: () => void;
}

export default function ImageZoomPanFrame({
  src,
  alt = "Image",
  rotation = 0,
  zoom: externalZoom,
  className = "",
  onError,
  onLoad,
}: ImageZoomPanFrameProps) {
  const [internalZoom, setInternalZoom] = useState<1 | 2>(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isTouchActive, setIsTouchActive] = useState(false);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [frameDimensions, setFrameDimensions] = useState({ width: 600, height: 600 });

  const zoom = externalZoom !== undefined ? externalZoom : internalZoom;

  const frameRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!frameRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        // Only update if dimensions are valid and actually changed
        if (width > 0 && height > 0 && (width !== frameDimensions.width || height !== frameDimensions.height)) {
          setFrameDimensions({ width, height });
        }
      }
    });

    observer.observe(frameRef.current);
    
    return () => observer.disconnect();
  }, [frameDimensions.width, frameDimensions.height]);

  const resetState = useCallback(() => {
    setInternalZoom(1);
    setPan({ x: 0, y: 0 });
    setIsTouchActive(false);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        resetState();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [resetState]);

  const calculateBounds = useCallback(() => {
    if (!imageSize || frameDimensions.width === 0 || frameDimensions.height === 0) return { maxX: 0, maxY: 0 };
    
    const rad = rotation * Math.PI / 180;
    const sin = Math.abs(Math.sin(rad));
    const cos = Math.abs(Math.cos(rad));
    
    const boundingWidth = imageSize.width * cos + imageSize.height * sin;
    const boundingHeight = imageSize.width * sin + imageSize.height * cos;
    
    const containScale = Math.min(
      frameDimensions.width / boundingWidth, 
      frameDimensions.height / boundingHeight
    );
    
    // Note: the transform rotates the image FIRST (from center), then scales it, then translates it.
    // The visual dimensions of the scaled image bounding box on screen are:
    const visualW = boundingWidth * containScale;
    const visualH = boundingHeight * containScale;
    
    const zoomedW = visualW * 2;
    const zoomedH = visualH * 2;
    
    const maxX = Math.max(0, (zoomedW - frameDimensions.width) / 2);
    const maxY = Math.max(0, (zoomedH - frameDimensions.height) / 2);
    
    return { maxX, maxY };
  }, [imageSize, frameDimensions, rotation]);

  const updatePanAndZoom = useCallback((clientX: number, clientY: number, forceZoom?: boolean) => {
    if (!frameRef.current || !imageSize) return;

    const rect = frameRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const inCenterArea = x >= frameDimensions.width * 0.25 && x <= frameDimensions.width * 0.75 &&
                         y >= frameDimensions.height * 0.25 && y <= frameDimensions.height * 0.75;

    let nextZoom = zoom;

    if (!forceZoom) {
      if (zoom === 1 && inCenterArea) {
        nextZoom = 2;
        setInternalZoom(2);
      }
    } else {
      nextZoom = 2;
      setInternalZoom(2);
    }

    if (nextZoom === 2) {
      const { maxX, maxY } = calculateBounds();
      // Add a buffer so the user doesn't have to reach the absolute edge of the container to pan completely.
      const buffer = 50; 
      
      // Calculate active area for mouse movement mapping (frame size minus buffer on each side)
      const activeWidth = Math.max(1, frameDimensions.width - (buffer * 2));
      const activeHeight = Math.max(1, frameDimensions.height - (buffer * 2));
      
      // Clamp x and y within the active area bounds
      const clampedX = Math.max(buffer, Math.min(x, frameDimensions.width - buffer)) - buffer;
      const clampedY = Math.max(buffer, Math.min(y, frameDimensions.height - buffer)) - buffer;

      // Linear mapping from [0, activeWidth] to [maxX, -maxX]
      const panX = maxX !== 0 ? maxX - (clampedX / activeWidth) * (maxX * 2) : 0;
      const panY = maxY !== 0 ? maxY - (clampedY / activeHeight) * (maxY * 2) : 0;
      
      setPan({ x: panX, y: panY });
    }
  }, [imageSize, zoom, calculateBounds, frameDimensions]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch') {
      setIsTouchActive(true);
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      updatePanAndZoom(e.clientX, e.clientY, true);
    }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch') {
      if (isTouchActive) {
        updatePanAndZoom(e.clientX, e.clientY, true);
      }
    } else {
      updatePanAndZoom(e.clientX, e.clientY);
    }
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch') {
      setIsTouchActive(false);
      resetState();
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    }
  };

  const onPointerLeave = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'touch') {
      resetState();
    }
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    if (onLoad) onLoad();
  };

  const getScaledDimensions = () => {
    if (!imageSize || frameDimensions.width === 0 || frameDimensions.height === 0) return { width: 0, height: 0 };
    
    const rad = rotation * Math.PI / 180;
    const sin = Math.abs(Math.sin(rad));
    const cos = Math.abs(Math.cos(rad));
    
    const boundingWidth = imageSize.width * cos + imageSize.height * sin;
    const boundingHeight = imageSize.width * sin + imageSize.height * cos;
    
    const containScale = Math.min(
      frameDimensions.width / boundingWidth, 
      frameDimensions.height / boundingHeight
    );
    
    return {
      width: imageSize.width * containScale,
      height: imageSize.height * containScale
    };
  };

  const scaledDims = getScaledDimensions();

  return (
    <div
      ref={frameRef}
      className={`relative overflow-hidden bg-white shadow-xl rounded-xl transition-shadow w-full h-full ${
        zoom === 2 ? (isTouchActive ? 'cursor-grabbing shadow-2xl' : 'cursor-grab shadow-2xl') : 'cursor-default'
      } ${className}`}
      style={{ 
        touchAction: 'none',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={onPointerLeave}
    >
      <div 
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px)`,
          transition: zoom === 1 ? 'transform 250ms cubic-bezier(0.25, 1, 0.5, 1)' : 'none',
        }}
      >
        <img
          src={src}
          alt={alt}
          onLoad={handleImageLoad}
          onError={onError}
          style={{
            width: imageSize ? scaledDims.width : 'auto',
            height: imageSize ? scaledDims.height : 'auto',
            maxWidth: imageSize ? 'none' : '100%',
            maxHeight: imageSize ? 'none' : '100%',
            flexShrink: 0,
            objectFit: 'contain',
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            transition: 'transform 250ms cubic-bezier(0.25, 1, 0.5, 1)',
            transformOrigin: 'center center',
            willChange: 'transform',
          }}
        />
      </div>
    </div>
  );
}
